import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";

const UcatCalculator = () => {
  const [scoringMode, setScoringMode] = useState(false);
  const [vrScore, setVrScore] = useState("");
  const [qrScore, setQrScore] = useState("");
  const [simpleDMScore, setSimpleDMScore] = useState("");
  const [twoMarkQuestions, setTwoMarkQuestions] = useState(0);
  const [correctOneMark, setCorrectOneMark] = useState("");
  const [correctTwoMarkFull, setCorrectTwoMarkFull] = useState("");
  const [correctTwoMarkPartial, setCorrectTwoMarkPartial] = useState("");
  
  const [vrScaled, setVrScaled] = useState("?");
  const [qrScaled, setQrScaled] = useState("?");
  const [dmScaled, setDmScaled] = useState("?");
  const [totalScore, setTotalScore] = useState("-");

  useEffect(() => {
    window.scrollTo(0, 0);
    calculateScores();
  }, [vrScore, qrScore, simpleDMScore, twoMarkQuestions, correctOneMark, correctTwoMarkFull, correctTwoMarkPartial, scoringMode]);

  const calculateScores = () => {
    let vr = calculateVrScore();
    let qr = calculateQrScore();
    let dm = calculateDmScore();
    
    setVrScaled(vr);
    setQrScaled(qr);
    setDmScaled(dm);
    
    if (vr !== "?" && qr !== "?" && dm !== "?") {
      setTotalScore(vr + qr + dm);
    } else {
      setTotalScore("-");
    }
  };

  const calculateVrScore = () => {
    const v = parseInt(vrScore);
    if (isNaN(v) || v < 0 || v > 44) return "?";
    
    if (v <= 5) return 300;
    if (v <= 7) return 330;
    if (v <= 8) return 350;
    if (v <= 10) return 370;
    if (v <= 11) return 400;
    if (v <= 12) return 430;
    if (v <= 14) return 450;
    if (v <= 15) return 470;
    if (v <= 17) return 500;
    if (v <= 18) return 530;
    if (v <= 20) return 550;
    if (v <= 22) return 570;
    if (v <= 23) return 600;
    if (v <= 25) return 630;
    if (v <= 27) return 650;
    if (v <= 29) return 670;
    if (v <= 31) return 700;
    if (v <= 32) return 730;
    if (v <= 33) return 750;
    if (v <= 34) return 770;
    if (v <= 36) return 800;
    if (v <= 38) return 830;
    if (v <= 39) return 850;
    if (v <= 41) return 870;
    if (v <= 44) return 900;
    
    return "?";
  };

  const calculateQrScore = () => {
    const v = parseInt(qrScore);
    if (isNaN(v) || v < 0 || v > 36) return "?";
    
    if (v <= 3) return 300;
    if (v <= 5) return 330;
    if (v <= 7) return 350;
    if (v <= 8) return 370;
    if (v <= 9) return 400;
    if (v <= 10) return 430;
    if (v <= 11) return 450;
    if (v <= 13) return 470;
    if (v <= 15) return 500;
    if (v <= 17) return 530;
    if (v <= 19) return 550;
    if (v <= 20) return 570;
    if (v <= 21) return 600;
    if (v <= 22) return 630;
    if (v <= 23) return 650;
    if (v <= 24) return 670;
    if (v <= 25) return 700;
    if (v <= 26) return 730;
    if (v <= 27) return 750;
    if (v <= 28) return 770;
    if (v <= 29) return 800;
    if (v <= 30) return 830;
    if (v <= 32) return 850;
    if (v <= 33) return 870;
    if (v <= 36) return 900;
    
    return "?";
  };

  const calculateDmScore = () => {
    if (!scoringMode) {
      // Simple scoring mode
      const v = parseInt(simpleDMScore);
      if (isNaN(v) || v < 0 || v > 35) return "?";
      
      if (v === 0) return 300;
      if (v === 35) return 900;
      
      const dm = 300 + (v / 35) * (900 - 300);
      return Math.round(dm / 10) * 10;
    } else {
      // Detailed scoring mode
      const totalQuestions = 35;
      const twoMark = parseInt(twoMarkQuestions) || 0;
      const oneMark = totalQuestions - twoMark;
      
      const correctOne = parseInt(correctOneMark) || 0;
      const correctTwoFull = parseInt(correctTwoMarkFull) || 0;
      const correctTwoPartial = parseInt(correctTwoMarkPartial) || 0;
      
      // Validate inputs
      if (correctOne > oneMark || 
          correctTwoFull > twoMark || 
          correctTwoPartial > twoMark ||
          (correctTwoFull + correctTwoPartial) > twoMark) {
        return "?";
      }
      
      // Calculate scores
      const oneMarkMaxScore = oneMark * 1;
      const twoMarkMaxScore = twoMark * 2;
      const totalMaxScore = oneMarkMaxScore + twoMarkMaxScore;
      
      const oneMarkActualScore = correctOne * 1;
      const twoMarkActualScore = (correctTwoFull * 2) + (correctTwoPartial * 1);
      const totalActualScore = oneMarkActualScore + twoMarkActualScore;
      
      // Scale to 300-900 range
      if (totalActualScore === 0) return 300;
      if (totalActualScore === totalMaxScore) return 900;
      
      const scaleFactor = (900 - 300) / totalMaxScore;
      const dm = 300 + totalActualScore * scaleFactor;
      
      return Math.round(dm / 10) * 10;
    }
  };

  const handleTwoMarkQuestionsChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    const clampedValue = Math.min(Math.max(value, 0), 35);
    setTwoMarkQuestions(clampedValue);
    
    // Reset related inputs if they exceed new maximums
    const oneMarkQuestions = 35 - clampedValue;
    if (parseInt(correctOneMark) > oneMarkQuestions) {
      setCorrectOneMark("");
    }
    if (parseInt(correctTwoMarkFull) > clampedValue) {
      setCorrectTwoMarkFull("");
    }
    if (parseInt(correctTwoMarkPartial) > clampedValue) {
      setCorrectTwoMarkPartial("");
    }
  };

  return (
    <div>
      <Card className="p-2 mt-3 card-main">
        <Card.Body>
          <Card.Title className="border-bottom pb-3 mb-3">UCAT Score Calculator</Card.Title>
          <div className="container">
            <div className="row">
              <div className="col">
                <div className="input-group mb-3">
                  <div className="input-group-prepend">
                    <span className="input-group-text">Verbal Reasoning Score</span>
                  </div>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="from 0 to 44" 
                    value={vrScore}
                    onChange={(e) => setVrScore(e.target.value)}
                  />
                </div>

                {/* Decision Making Section */}
                <div className="card mb-3">
                  <div className="card-header bg-base text-white d-flex justify-content-between align-items-center">
                    <span>Decision Making Score Calculator</span>
                    <div className="custom-control custom-switch">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        id="scoringMode" 
                        checked={scoringMode}
                        onChange={() => setScoringMode(!scoringMode)}
                      />
                      <label className="custom-control-label text-white mx-2" htmlFor="scoringMode">
                        Complex Mode
                      </label>
                    </div>
                  </div>
                  <div className="card-body">
                    {/* Simple Scoring Mode (Default) */}
                    {!scoringMode && (
                      <div id="simpleScoring">
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">Score (out of 35)</span>
                          </div>
                          <input 
                            type="number" 
                            className="form-control" 
                            placeholder="Enter score (0-35)" 
                            min="0" 
                            max="35" 
                            value={simpleDMScore}
                            onChange={(e) => setSimpleDMScore(e.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Detailed Scoring Mode */}
                    {scoringMode && (
                      <div id="detailedScoring">
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">Number of 2 Mark Questions</span>
                          </div>
                          <input 
                            type="number" 
                            className="form-control" 
                            placeholder="Enter number (0-35)" 
                            min="0" 
                            max="35" 
                            value={twoMarkQuestions}
                            onChange={handleTwoMarkQuestionsChange}
                          />
                          <small className="form-text text-muted d-block w-100 text-center mt-2">
                            One Mark Questions: {35 - twoMarkQuestions}
                          </small>
                        </div>
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">Correct One Mark Answers</span>
                          </div>
                          <input 
                            type="number" 
                            className="form-control" 
                            placeholder="Number of correct 1 mark answers" 
                            max={35 - twoMarkQuestions}
                            value={correctOneMark}
                            onChange={(e) => setCorrectOneMark(e.target.value)}
                          />
                        </div>
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">Fully Correct Two Mark</span>
                          </div>
                          <input 
                            type="number" 
                            className="form-control" 
                            placeholder="Number of fully correct 2 mark answers" 
                            max={twoMarkQuestions}
                            value={correctTwoMarkFull}
                            onChange={(e) => setCorrectTwoMarkFull(e.target.value)}
                          />
                        </div>
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">Partially Correct Two Mark</span>
                          </div>
                          <input 
                            type="number" 
                            className="form-control" 
                            placeholder="Number of partially correct 2 mark answers" 
                            max={twoMarkQuestions}
                            value={correctTwoMarkPartial}
                            onChange={(e) => setCorrectTwoMarkPartial(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="input-group mb-3">
                  <div className="input-group-prepend">
                    <span className="input-group-text">Quantitative Reasoning Score</span>
                  </div>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="from 0 to 36" 
                    value={qrScore}
                    onChange={(e) => setQrScore(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <ul className="list-group">
                  <li className="list-group-item text-center"><b>Calculated Scores:</b></li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Verbal Reasoning: 
                    <span className="badge bg-base rounded-pill">{vrScaled}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Decision Making: 
                    <span className="badge bg-base rounded-pill">{dmScaled}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Quantitative Reasoning: 
                    <span className="badge bg-base rounded-pill">{qrScaled}</span>
                  </li>
                  <li className="list-group-item active d-flex justify-content-between align-items-center">
                    Total (out of 2700): <b>{totalScore}</b>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UcatCalculator;